import type { StateData } from '@/lib/become-an-agent-types';

/**
 * CostComparison
 *
 * Side-by-side annual cost breakdown for the two paths into a travel-agent
 * career: building an independent agency from scratch vs. joining a host.
 * Editorial table treatment — neutral pillar names on the left, two cost
 * columns showing typical industry ranges (independent) vs LuxRally
 * inclusive coverage (hosted).
 *
 * SEO: H2 matches the high-volume search query "how much does it cost to
 * become a travel agent in {state}".
 */

interface CostRow {
    category: string;
    independent: string;
    hosted: string;
}

const COST_ROWS: CostRow[] = [
    {
        category: 'Seller of Travel registration',
        independent: '$100–$300/yr (where required)',
        hosted: '$51.50/yr (FL affidavit only)',
    },
    {
        category: 'IATA / ARC accreditation',
        independent: '$1,200+/yr',
        hosted: 'Included',
    },
    {
        category: 'Consortium membership',
        independent: '$500–$2,000/yr',
        hosted: 'Included',
    },
    {
        category: 'Errors & omissions insurance',
        independent: '$500–$2,000/yr',
        hosted: 'Covered by agency',
    },
    {
        category: 'Surety bonds (where required)',
        independent: '$1,000–$5,000 setup',
        hosted: 'Covered by agency',
    },
    {
        category: 'Client trust accounts',
        independent: '$500–$2,000/yr',
        hosted: 'Not needed',
    },
    {
        category: 'Legal & compliance review',
        independent: '$2,000–$10,000 setup, plus ongoing',
        hosted: 'Covered by agency',
    },
    {
        category: 'Brand, website, booking tech',
        independent: '$500–$5,000 setup, plus ongoing',
        hosted: 'FastBook + CRM included',
    },
    {
        category: 'Training',
        independent: '$300–$3,000 (industry courses)',
        hosted: 'Free (four-part curriculum)',
    },
    {
        category: 'In-house concierge support',
        independent: '$150,000+/yr (FT staff)',
        hosted: 'Free for clients booking $1,000+/night (excl. cruise & all-inclusive)',
    },
];

export function CostComparison({ state }: { state: StateData }) {
    return (
        <section className="bg-[#fdfaf3]">
            <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20 lg:py-24">
                <div className="text-center">
                    <p className="lux-caps lux-caps-sm text-[#0e2727]/55">
                        Cost comparison
                    </p>
                    <h2
                        className="font-didot mx-auto mt-3 max-w-3xl text-[#0b2222]"
                        style={{ fontSize: 'clamp(1.85rem, 2.8vw, 2.5rem)', lineHeight: 1.1 }}
                    >
                        How much does it cost to become a travel agent in {state.state}?
                    </h2>
                    <p
                        className="font-didot mx-auto mt-5 max-w-xl text-[#0e2727]/80 italic"
                        style={{ fontSize: 'clamp(1rem, 1.3vw, 1.2rem)', lineHeight: 1.5 }}
                    >
                        Building an independent agency runs a few thousand dollars a year before
                        your first booking. The hosted path takes most of that off your plate.
                    </p>
                </div>

                {/* Framing — the agent's tiny annual cost is small because the
                    agency absorbed the rest, not because the rest doesn't exist. */}
                <div className="mx-auto mt-10 max-w-3xl border-l-2 border-[#7C4D2E] pl-5">
                    <p className="text-[#0e2727]/75" style={{ letterSpacing: '0.01em' }}>
                        <span className="text-[#7C4D2E]">&rarr;</span> The asymmetry below is the
                        point.
                    </p>
                    <p
                        className="font-didot mt-2 text-[#0b2222] italic"
                        style={{ fontSize: 'clamp(1.05rem, 1.4vw, 1.25rem)', lineHeight: 1.5 }}
                    >
                        LuxRally has spent years building proprietary booking technology, posting
                        hundreds of thousands in bonds to secure direct credit lines with major
                        wholesalers and securing credentials, and earning seats in preferred-partner
                        programs that take large revenue volumes to qualify for. Replicating it
                        independently runs into the millions &mdash; before a single booking.
                    </p>
                </div>

                {/* Desktop table */}
                <div className="mt-12 hidden md:block">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-[#0e2727]/15 [&>th]:lux-caps [&>th]:lux-caps-sm [&>th]:pb-4 [&>th]:text-[#0e2727]/55 [&>th]:font-normal">
                                <th className="text-left">Cost</th>
                                <th className="text-left">Independent path</th>
                                <th className="text-left">Hosted (LuxRally)</th>
                            </tr>
                        </thead>
                        <tbody className="text-[#0e2727]">
                            {COST_ROWS.map((row) => (
                                <tr
                                    key={row.category}
                                    className="border-b border-[#0e2727]/10"
                                >
                                    <td className="py-4 pr-6 align-top text-sm leading-snug text-[#0e2727]/80">
                                        {row.category}
                                    </td>
                                    <td className="py-4 pr-6 align-top text-sm leading-snug text-[#0e2727] tabular-nums">
                                        {row.independent}
                                    </td>
                                    <td className="py-4 align-top text-sm leading-snug text-[#0b2222] tabular-nums [&>strong]:font-semibold">
                                        {row.hosted}
                                    </td>
                                </tr>
                            ))}
                            <tr className="[&>td]:font-didot [&>td]:py-5 [&>td]:text-lg [&>td]:text-[#0b2222]">
                                <td className="pr-6 align-top">
                                    <span className="lux-caps lux-caps-sm font-normal text-[#0e2727]/55">
                                        Typical annual
                                    </span>
                                </td>
                                <td className="pr-6 align-top tabular-nums">
                                    $5,000–$20,000+
                                </td>
                                <td className="align-top font-semibold tabular-nums">
                                    ~$51.50
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Mobile — stacked */}
                <ul className="mt-10 space-y-5 md:hidden">
                    {COST_ROWS.map((row) => (
                        <li
                            key={row.category}
                            className="border-b border-[#0e2727]/10 pb-5"
                        >
                            <p className="lux-caps lux-caps-sm text-[#0e2727]/55">
                                {row.category}
                            </p>
                            <dl className="mt-2 grid grid-cols-2 gap-x-4 text-sm">
                                <div>
                                    <dt className="text-xs text-[#0e2727]/55">Independent</dt>
                                    <dd className="mt-0.5 text-[#0e2727] tabular-nums">
                                        {row.independent}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-[#0e2727]/55">Hosted (LuxRally)</dt>
                                    <dd className="mt-0.5 text-[#0b2222] tabular-nums">
                                        {row.hosted}
                                    </dd>
                                </div>
                            </dl>
                        </li>
                    ))}
                    <li className="font-didot text-base text-[#0b2222]">
                        <p className="lux-caps lux-caps-sm font-normal text-[#0e2727]/55">
                            Typical annual
                        </p>
                        <dl className="mt-2 grid grid-cols-2 gap-x-4">
                            <div>
                                <dt className="text-xs text-[#0e2727]/55">Independent</dt>
                                <dd className="mt-0.5 tabular-nums">$5,000–$20,000+</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-[#0e2727]/55">Hosted (LuxRally)</dt>
                                <dd className="mt-0.5 font-semibold tabular-nums">~$51.50</dd>
                            </div>
                        </dl>
                    </li>
                </ul>

                <p className="mx-auto mt-10 max-w-3xl text-xs leading-[1.6] text-[#0e2727]/55">
                    Independent ranges reflect publicly listed industry pricing for IATA/ARC
                    accreditation, consortium dues, and typical agency operating costs. The
                    training row&apos;s $300&ndash;$3,000 range covers core CTA/CTC industry
                    courses; specialty programs (luxury, destination certifications, supplier
                    academies) can extend to $10,000+. Seller of Travel fees apply where required
                    (California, Florida, Hawaii, Washington); other states have lower or no
                    registration costs. LuxRally figures reflect advisor-incurred costs only
                    &mdash; the agency carries the licensing, consortium, and tech infrastructure.
                </p>
            </div>
        </section>
    );
}
